/**
 * Générateur — Il utilise les puissances d'exposants strictement positifs d'un
 * nombre pour simplifier l'écriture des produits.
 * Attendu ID : 141 | Niveau : 4ème | Domaine : Nombres (desc 11, O6)
 *
 * Source doc prof (Cluster 1, O6) : produit de facteurs → aⁿ×bᵐ, signe d'une
 * puissance, calcul de aⁿ, puissance d'une fraction, écrire avec une puissance
 * d'un nombre premier. Plan de cartes validé 2026-07-14, revu 2026-09-22 :
 *
 *   Carte « produit » — a×b×a×… = aⁿ × bᵐ (texte/saisie, exposants en trous)
 *     une base ou deux, base négative entre parenthèses une fois sur deux,
 *     exposants 2 à 6, au plus 8 facteurs.
 *   Carte « condenser » — 7×7×7×7×7 = ? (qcm_unique) : le QCM de l'attendu,
 *     distracteurs nommés (échange base/exposant, base × exposant, somme des facteurs).
 *   Carte « signe » — le résultat est positif ou négatif ? (tableau_vf)
 *     PAS de drag (collision positif/négatif ×2 trous, leçon 5e) ; six lignes distinctes.
 *   Carte « calcul » — aⁿ, (−a)ⁿ (texte/saisie, résultat ENTIER ≤ 1 000, 27 couples)
 *   Carte « fraction » — (p/q)ⁿ = num/den irréductible (texte/saisie, signe séparé)
 *   Carte « ecrire » — m = (nombre premier)ⁿ, exposant imposé (texte/saisie)
 *     2² à 2⁹, 3² à 3⁵, 5² à 5³, 7² à 7³ : 16 nombres × 3 formulations d'énoncé
 *     (motif de gen_140) = 48 empreintes, au-dessus du seuil d'unicité (20).
 *
 * Revue 2026-09-22 : carte « expzero » SUPPRIMÉE (la réponse était dans
 * l'énoncé : a⁰ = 1 rappelé, a¹ = la base recopiée ; et l'intitulé dit
 * « strictement positifs ») ; carte « unites » (chiffre des unités de aⁿ,
 * exposants 15 à 99, distracteurs au hasard) REMPLACÉE par « condenser », QCM
 * sur l'attendu ; « calcul » plafonné à 1 000 (9⁶ = 531 441 et 2²⁵ ne se
 * calculent pas de tête) ; « ecrire » resserré (reconnaître 32 768 = 2¹⁵ n'est
 * pas une reconnaissance), base 7 ajoutée (49, 343) et trois formulations
 * d'énoncé tirées au hasard pour l'unicité, sans élargir le domaine (motif de
 * gen_140) ; « produit » : base unique autorisée (le cœur de l'intitulé,
 * jamais produit avant), base négative, exposant jusqu'à 6 ; « signe » : deux
 * lignes identiques ne peuvent plus réduire le tableau ; parenthèses de
 * consigne → phrases, celles qui donnaient la méthode ou la réponse parties
 * avec leurs cartes. Distracteurs du QCM : « base × exposant » (7 × 5) et
 * « somme des facteurs » (7 + 7 + 7 + 7 + 7) ont la même VALEUR sous deux
 * écritures, familles demandées par la revue (mode auto, À ARBITRER : 7⁴,
 * l'exposant qui compte les signes ×, serait une famille de valeur distincte).
 *
 * Revue 2026-09-23 : typographie. Toute puissance AFFICHÉE passe par les
 * helpers KaTeX de generators/utils.js (puissTex, tex, fracTrouTex) ; le
 * helper exp() de nombres.js (exposants Unicode) et les <sup> ne sont plus
 * utilisés. Les deux phrases à trous sont en mode mathématique global (la
 * ligne ENTIÈRE entre $, technique de gen_205 / gen_924) : dans « produit »
 * l'exposant en trou est protégé par un groupe, {{2}}^{{{3}}}, et la ligne
 * est coupée en deux au-delà de six facteurs (limite d'impression : 60
 * caractères visibles) ; dans « fraction » le résultat est un \dfrac à deux
 * trous. Dans le QCM « condenser » les QUATRE choix sont en KaTeX, pas
 * seulement les deux puissances : sinon la police trahirait la clé. La lettre
 * n de la carte « ecrire » est en KaTeX. Les réponses saisies restent du
 * texte brut.
 *
 * Composition 2026-09-23 (règle prof, tableaux vrai-faux) : « signe » passe
 * de cinq lignes à quatre, sinon c'est trop long à lire ; la banque garde ses
 * six lignes et, avec quatre lignes sur six, un tableau d'un seul signe
 * devenait possible : il est remplacé par construction (une ligne de l'autre
 * signe reprise en banque), les deux réponses sont toujours présentes. Même
 * jour : les lignes -aⁿ sont groupées, {-3^{4}}, pour que KaTeX rende le moins
 * unaire et serré comme dans (-3)⁴ (règle du dépôt, déjà appliquée en 139).
 *
 * Tout par construction ; puissances entières exactes (Number), fractions
 * irréductibles par p, q premiers entre eux.
 *
 * Calculatrice 2026-09-23 : les cartes « calcul » et « fraction » portent
 * contenu.calculatrice (décision prof). Le savoir évalué est le SENS de la
 * puissance, pas l'arithmétique : 3⁶ = 729 et (7/6)⁴ = 2401/1296 se calculent,
 * ils ne se savent pas. « ecrire » reste SANS, parce que le travail y EST la
 * reconnaissance et qu'une calculatrice permettrait de diviser en boucle ;
 * « signe » aussi, son énoncé disant déjà « sans le calculer ».
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeTexteTrou, makeVraiFaux, tex, puissTex, fracTrouTex,
} from '../../../utils';
import { pow, pgcd } from './nombres';

const ATTENDU_ID = 141;

const L_PRODUIT = 'Écrire un produit avec des puissances';
const L_CONDENSER = 'Reconnaître l\'écriture en puissance d\'un produit';
const L_SIGNE = 'Déterminer le signe d\'une puissance';
const L_CALCUL = 'Calculer une puissance';
const L_FRACTION = 'Calculer la puissance d\'une fraction';
const L_ECRIRE = 'Écrire un nombre comme puissance d\'un premier';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'exactement 3 distracteurs + la bonne réponse, tous distincts : base ≠ exposant (l\'échange vaudrait la clé) et jamais 2⁴ (4² = 2⁴ = 16, l\'échange serait juste en valeur)',
        'aucun distracteur au hasard : chaque choix nomme une erreur d\'élève (revue 2026-09-22, l\'ancien QCM « chiffre des unités » tirait ses distracteurs au hasard)',
        'la bonne réponse est unique par construction : aⁿ > a × n pour a ≥ 2 et n ≥ 3, et aⁿ ≠ nᵃ hors {2, 4}',
    ],
    condenser_qcm_unique: {
        support: 'base 2 à 9, exposant 3 à 6 (27 couples)',
        fixes: {
            echange: 'nᵃ au lieu de aⁿ : la base et l\'exposant échangés (5⁷ pour 7 × 7 × 7 × 7 × 7)',
            base_fois_exposant: 'a × n : la puissance lue comme un produit (7 × 5)',
            somme_des_facteurs: 'a + a + … + a : le signe × lu comme un + (7 + 7 + 7 + 7 + 7) ; même valeur que a × n, deux écritures distinctes',
        },
        rotation: 'aucune : les trois familles sont toujours définies',
    },
};

// ═════════ Carte « produit » — a×b×a×… = aⁿ × bᵐ ═════════

const genProduitTrous = () => {
    const uneBase = randomChoice([false, true]);            // une base répétée : le cœur de l'intitulé
    const bases = shuffle([2, 3, 4, 5, 6, 7, 8, 9]).slice(0, uneBase ? 1 : 2);
    const eA = randInt(2, 6);
    const expos = uneBase ? [eA] : [eA, randInt(2, Math.min(6, 8 - eA))];   // au plus 8 facteurs
    const iNeg = randomChoice([false, true]) ? randInt(0, bases.length - 1) : -1;   // une base négative une fois sur deux
    const aff = (i) => (i === iNeg ? `(-${bases[i]})` : String(bases[i]));
    const facteurs = shuffle(bases.flatMap((b, i) => Array(expos[i]).fill(aff(i))));
    const produit = facteurs.join(' \\times ');
    const condense = bases.map((b, i) => `${aff(i)}^{{{${expos[i]}}}}`).join(' \\times ');
    // Phrase en mode mathématique global : la ligne ENTIÈRE entre $ (hasMathLines),
    // l'exposant en trou protégé par un groupe, {{2}}^{{{3}}} (motif gen_205).
    // Une ligne imprimée se coupe au-delà de 60 caractères visibles
    // (Print/helpers/expressions.js) et un produit de sept ou huit facteurs
    // avec une base négative peut les dépasser : au-delà de six facteurs, le
    // produit et son écriture condensée prennent deux lignes.
    const phrase = facteurs.length > 6
        ? `$${produit}$\n$= ${condense}$`
        : `$${produit} = ${condense}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On veut écrire le produit ${tex(produit)} ${uneBase ? 'sous la forme d\'une puissance' : 'à l\'aide de puissances'}.</p>`
        + `<p><b>Complète ${uneBase ? 'l\'exposant' : 'les exposants'}.</b></p>`,
        { ...opts(L_PRODUIT), mode: 'saisie' });
    payload._meta.params = { bases, expos, iNeg };
    return payload;
};

// ═════════ Carte « condenser » — 7×7×7×7×7 = 7⁵ (QCM) ═════════

// Couples (base, exposant) du QCM : base 2 à 9, exposant 3 à 6, base ≠ exposant
// (l'échange donnerait la clé) et jamais 2⁴ (4² = 2⁴ = 16 : l'échange serait
// juste en valeur). 27 couples.
const COUPLES_CONDENSER = (() => {
    const out = [];
    for (let a = 2; a <= 9; a++) {
        for (let n = 3; n <= 6; n++) if (a !== n && !(a === 2 && n === 4)) out.push([a, n]);
    }
    return out;
})();

const genCondenserQcm = () => {
    const [a, n] = randomChoice(COUPLES_CONDENSER);
    const produit = tex(Array(n).fill(String(a)).join(' \\times '));
    const correct = puissTex(a, n);
    // Les quatre choix en KaTeX, pas seulement les puissances : deux choix en
    // police KaTeX et deux en police de l'interface désigneraient la clé.
    const choix = [
        correct,
        puissTex(n, a),                                   // échange base / exposant
        tex(`${a} \\times ${n}`),                         // la base multipliée par l'exposant
        tex(Array(n).fill(String(a)).join(' + ')),        // la somme des facteurs
    ];
    const texte = `<p>On veut écrire plus court le produit ${produit}.</p>`
        + `<p><b>Quelle écriture est égale à ${produit} ?</b></p>`;
    const payload = makeQcm(choix, correct, texte, opts(L_CONDENSER));
    payload._meta.params = { a, n, distracteurs: ['echange', 'base_fois_exposant', 'somme_des_facteurs'] };
    return payload;
};

// ═════════ Carte « signe » — positif ou négatif ? ═════════

// Fractions propres irréductibles p/q, p ∈ [1 ; 6], q ∈ [2 ; 7], pour la ligne (-p/q)ⁿ.
const FRACTIONS_PROPRES = (() => {
    const out = [];
    for (let q = 2; q <= 7; q++) for (let p = 1; p < q; p++) if (pgcd(p, q) === 1) out.push([p, q]);
    return out;
})();

const genSigneTableau = () => {
    const couple = () => ({ a: randInt(2, 9), n: randInt(2, 8) });
    // Deux couples distincts pour une même forme de ligne : nouveau tirage en cas
    // d'égalité (borné), puis repli déterministe sur l'exposant suivant (même signe).
    const deuxCouples = () => {
        const c1 = couple();
        let c2 = couple();
        for (let essai = 0; essai < 10 && c1.a === c2.a && c1.n === c2.n; essai++) c2 = couple();
        if (c1.a === c2.a && c1.n === c2.n) c2 = { a: c1.a, n: c1.n === 8 ? 2 : c1.n + 1 };
        return [c1, c2];
    };
    const [p1, p2] = deuxCouples();
    const [m1, m2] = deuxCouples();
    const b = randInt(2, 9); const nb = randInt(2, 9);
    const [p, q] = randomChoice(FRACTIONS_PROPRES); const nf = randInt(2, 9);
    // Six lignes distinctes par construction (les formes diffèrent entre elles,
    // les deux couples d'une même forme aussi). Le groupe autour de -a^n rend le
    // moins unaire : sans lui, le \mathrm{} que tex() insère en tête en fait un
    // opérateur binaire et KaTeX l'espace (« − 3⁴ »), alors que (-3)⁴ se rend
    // serré ; or c'est justement l'écart que la carte fait lire.
    const banque = [
        { txt: puissTex(p1.a, p1.n), pos: true },
        { txt: puissTex(p2.a, p2.n), pos: true },
        { txt: tex(`{-${m1.a}^{${m1.n}}}`), pos: false },
        { txt: tex(`{-${m2.a}^{${m2.n}}}`), pos: false },
        { txt: puissTex(`(-${b})`, nb), pos: nb % 2 === 0 },
        { txt: tex(`\\left(-\\dfrac{${p}}{${q}}\\right)^{${nf}}`), pos: nf % 2 === 0 },
    ];
    // Quatre lignes (règle prof 2026-09-23 : quatre au plus). Les deux classes
    // sont garanties : si les quatre tirées ont le même signe (les deux
    // positives fixes avec (-b)ⁿ et (-p/q)ⁿ d'exposant pair, ou le symétrique),
    // la dernière est remplacée par une ligne de l'autre signe restée en banque ;
    // il en reste toujours une, la banque a deux lignes de chaque signe fixe.
    const sel = shuffle(banque).slice(0, 4);
    if (sel.every(x => x.pos === sel[0].pos)) {
        sel[3] = banque.find(x => !sel.includes(x) && x.pos !== sel[0].pos);
    }
    const payload = makeVraiFaux(sel.map(x => x.txt), sel.map(x => (x.pos ? 0 : 1)),
        '<p><b>Pour chaque nombre, indique si son résultat est positif ou négatif, sans le calculer.</b></p>',
        { ...opts(L_SIGNE), colonnes: ['Positif', 'Négatif'] });
    payload._meta.params = {};
    return payload;
};

// ═════════ Carte « calcul » — aⁿ, (−a)ⁿ (résultat entier ≤ 1 000) ═════════

// Couples (base, exposant) dont la valeur reste ≤ 1 000 : 27 couples, le
// double avec le signe. Personne ne calcule 9⁶ = 531 441 de tête.
const COUPLES_CALCUL = (() => {
    const out = [];
    for (let a = 2; a <= 9; a++) for (let e = 2; pow(a, e) <= 1000; e++) out.push([a, e]);
    return out;
})();

const genCalculSaisie = () => {
    const negatif = randomChoice([false, false, true]);
    const [a, e] = randomChoice(COUPLES_CALCUL);
    const val = pow(a, e) * (negatif && e % 2 === 1 ? -1 : 1);
    const affBase = negatif ? `(-${a})` : String(a);
    const texte = `<p><b>Calcule ${puissTex(affBase, e)}.</b></p>`
        + (negatif ? '<p>Un résultat négatif s\'écrit avec le signe « - ».</p>' : '');
    const payload = makeSaisie(String(val), texte, opts(L_CALCUL));
    // Calculatrice AUTORISÉE (revue 2026-09-23) : le savoir évalué est le sens de la puissance, pas l'arithmétique : 3⁶ = 729 se calcule, il ne se sait pas.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.params = { a, e, negatif, val };
    return payload;
};

// ═════════ Carte « fraction » — (p/q)ⁿ = num/den irréductible ═════════

const genFractionTrous = () => {
    let p; let q;
    do { p = randInt(2, 7); q = randInt(2, 6); } while (pgcd(p, q) !== 1 || p === q);
    const n = randInt(2, 4);
    const negatif = randomChoice([false, true]);
    const num = pow(p, n) * (negatif && n % 2 === 1 ? -1 : 1);
    const den = pow(q, n);
    const puissance = `\\left(${negatif ? '-' : ''}\\dfrac{${p}}{${q}}\\right)^{${n}}`;
    // Ligne entière en mode mathématique ; numérateur et dénominateur en trous
    // dans un \dfrac (triple accolade, cf. fracTrouTex).
    const phrase = `$${puissance} = ${fracTrouTex(`{{${num}}}`, `{{${den}}}`)}$`;
    const payload = makeTexteTrou(phrase,
        // « dénominateur positif » n'est pas décoratif : le correcteur compare
        // trou par trou, donc 64 sur -125 serait compté faux alors que c'est
        // mathématiquement juste. Formulation alignée sur les attendus 147 et
        // 148. L'irréductibilité, elle, est garantie par construction (p et q
        // premiers entre eux, donc pⁿ et qⁿ aussi) : le mot reste pour dire à
        // l'élève de ne pas amplifier, il n'y a qu'une écriture possible.
        `<p>On calcule ${tex(puissance)} et on donne le résultat en <b>fraction irréductible</b>, avec un <b>dénominateur positif</b>.</p>`
        + '<p><b>Complète le numérateur et le dénominateur.</b></p>'
        + '<p>Un numérateur négatif s\'écrit avec le signe « - ».</p>',
        { ...opts(L_FRACTION), mode: 'saisie' });
    // Calculatrice AUTORISÉE (revue 2026-09-23) : puissance d'une fraction : (7/6)⁴ = 2401/1296, le calcul n'est pas le savoir évalué.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.params = { p, q, n, negatif, num, den };
    return payload;
};

// ═════════ Carte « ecrire » — m = (premier)ⁿ, exposant imposé ═════════

// Exposant maximal par base : 2² à 2⁹, 3² à 3⁵, 5² à 5³, 7² à 7³ (des puissances
// qu'un élève de 4e RECONNAÎT ; 32 768 = 2¹⁵ n'est pas une reconnaissance) :
// 16 nombres. Le domaine étant petit par nature, l'énoncé varie sa FORMULATION
// (motif de gen_140) : 16 nombres × 3 formulations = 48 empreintes, au-dessus
// du seuil d'unicité (20) sans élargir le domaine.
const EXPOSANT_MAX_ECRIRE = { 2: 9, 3: 5, 5: 3, 7: 3 };

// Trois formulations du même geste, également claires : la base et le nombre
// sont donnés, l'exposant n est demandé, aucune ne dit comment le trouver.
const ECRIRE_TEMPLATES = [
    (m, base) => `<p>Écris ${m} sous la forme d'une puissance de ${base}.</p>`
        + `<p><b>${tex(`${m} = ${base}^{n}`)}. Quel est l'exposant ${tex('n')} ?</b></p>`,
    (m, base) => `<p>${m} est une puissance de ${base}.</p>`
        + `<p><b>Quel est l'exposant ${tex('n')} tel que ${tex(`${m} = ${base}^{n}`)} ?</b></p>`,
    (m, base) => `<p>On veut écrire ${m} avec une puissance de ${base}.</p>`
        + `<p><b>Quel exposant ${tex('n')} faut-il pour que ${tex(`${base}^{n} = ${m}`)} ?</b></p>`,
];

const genEcrireSaisie = () => {
    const base = randomChoice([2, 3, 5, 7]);
    const e = randInt(2, EXPOSANT_MAX_ECRIRE[base]);
    const m = pow(base, e);
    const payload = makeSaisie(String(e), randomChoice(ECRIRE_TEMPLATES)(m, base), opts(L_ECRIRE));
    payload._meta.params = { base, e, m };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    produit_texte_trou_saisie: genProduitTrous,
    condenser_qcm_unique: genCondenserQcm,
    signe_tableau_vf: genSigneTableau,
    calcul_texte: genCalculSaisie,
    fraction_texte_trou_saisie: genFractionTrous,
    ecrire_texte: genEcrireSaisie,
};

const FORMAT_LABELS = {
    texte_trou_saisie: 'Saisie trous', texte: 'Saisie', tableau_vf: 'Vrai/Faux', qcm_unique: 'QCM',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'produit', label: L_PRODUIT, formatsList: buildFormats('produit') },
    { id: 'condenser', label: L_CONDENSER, formatsList: buildFormats('condenser') },
    { id: 'signe', label: L_SIGNE, formatsList: buildFormats('signe') },
    { id: 'calcul', label: L_CALCUL, formatsList: buildFormats('calcul') },
    { id: 'fraction', label: L_FRACTION, formatsList: buildFormats('fraction') },
    { id: 'ecrire', label: L_ECRIRE, formatsList: buildFormats('ecrire') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
